import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const listingsSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  items: z
    .array(
      z.object({
        ref: z.string().min(1).max(40),
        sector: z.string().min(1).max(120),
        location: z.string().max(80).optional(),
        description: z.string().max(800),
        meta: z
          .array(
            z.object({
              label: z.string().min(1).max(40),
              value: z.string().min(1).max(40),
            }),
          )
          .min(2)
          .max(6),
        ctaLabel: z.string().max(80).default('Solicitar info (NDA)'),
        ctaHref: z.string().max(500).default('/contacto'),
      }),
    )
    .min(1)
    .max(24),
});

export type ListingsData = z.infer<typeof listingsSchema>;

export function ListingsRenderer({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  items,
}: ListingsData) {
  const safeItems = Array.isArray(items) ? items : [];
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  return (
    <section className="v5-light v5-section v5-listings-section">
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-listing-h2">{title}</h2>
          {subtitle ? <p className="v5-listing-sub">{subtitle}</p> : null}
        </div>
        <div className="v5-listings-grid">
          {safeItems.map((item, i) => (
            <article key={i} className="v5-listing-card">
              <div className="v5-listing-head">
                <div className="v5-listing-ref">Ref. {item.ref}</div>
                <div className="v5-listing-sector">{item.sector}</div>
              </div>
              <div className="v5-listing-meta-grid">
                {(item.meta ?? []).map((m, j) => (
                  <div key={j} className="v5-listing-meta-item">
                    <span className="v5-listing-meta-lbl">{m.label}</span>
                    <span className="v5-listing-meta-val">{m.value}</span>
                  </div>
                ))}
              </div>
              <div className="v5-listing-desc">{item.description}</div>
              <div className="v5-listing-foot">
                {item.location ? (
                  <div className="v5-listing-loc">📍 {item.location}</div>
                ) : (
                  <div></div>
                )}
                <a href={item.ctaHref} className="v5-listing-cta">
                  {item.ctaLabel} →
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export const listingsBlock: BlockDefinition<ListingsData> = {
  meta: {
    type: 'listings',
    label: 'Listings empresas en venta (v5)',
    description: 'Catálogo de empresas anonimizadas con ref, sector, meta-grid de cifras y CTA NDA.',
    category: 'collection',
    icon: 'building',
  },
  schema: listingsSchema,
  defaults: {
    eyebrow: '● Selección de oportunidades',
    title: 'Una muestra del catálogo activo',
    subtitle: 'Cada ficha es una empresa real anonimizada. Información completa bajo NDA.',
    align: 'left',
    items: [
      {
        ref: 'REF-2026-0142',
        sector: 'Industrial · Metal',
        location: 'Cataluña',
        description: 'Cartera diversificada · Equipo directivo en plantilla · Contratos plurianuales · Motivo: jubilación.',
        meta: [
          { label: 'Facturación', value: '€4,2M' },
          { label: 'EBITDA', value: '€620K' },
          { label: 'Empleados', value: '28' },
          { label: 'Operación', value: 'Venta 100%' },
        ],
        ctaLabel: 'Solicitar info (NDA)',
        ctaHref: '/contacto#agendar-ma',
      },
      {
        ref: 'REF-2026-0138',
        sector: 'Hostelería · Restauración',
        location: 'Madrid',
        description: 'Ubicación premium · Carta consolidada · Local en propiedad o alquiler · Motivo: cambio profesional.',
        meta: [
          { label: 'Facturación', value: '€1,8M' },
          { label: 'EBITDA aj.', value: '€310K' },
          { label: 'Empleados', value: '22' },
          { label: 'Operación', value: 'Venta 100%' },
        ],
        ctaLabel: 'Solicitar info (NDA)',
        ctaHref: '/contacto#agendar-ma',
      },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'items',
      label: 'Listings',
      type: 'array',
      max: 24,
      itemFields: [
        { name: 'ref', label: 'Referencia', type: 'text', required: true, max: 40 },
        { name: 'sector', label: 'Sector', type: 'text', required: true, max: 120 },
        { name: 'location', label: 'Ubicación', type: 'text', max: 80 },
        { name: 'description', label: 'Descripción', type: 'textarea', required: true, max: 800 },
        {
          name: 'meta',
          label: 'Meta (facturación, EBITDA, etc.)',
          type: 'array',
          max: 6,
          itemFields: [
            { name: 'label', label: 'Label', type: 'text', required: true, max: 40 },
            { name: 'value', label: 'Valor', type: 'text', required: true, max: 40 },
          ],
        },
        { name: 'ctaLabel', label: 'Texto CTA', type: 'text', max: 80 },
        { name: 'ctaHref', label: 'URL CTA', type: 'url' },
      ],
    },
  ],
  Renderer: ListingsRenderer,
};
